import Vue from 'vue'
import VueRouter from 'vue-router'
import { validateAndResolveNewRoute } from '@lib/vue-router'
import { localizedRoute } from '@helper/localizedRouting'
import i18n from '@root/src/javascripts/i18n'
import { routes } from '@vuecmp/routes'

const pathRegex = /([\/:]?)(\w+)([\/:]?)/g

Vue.use(VueRouter)

const generateCatchAllRoute = () => {
  // eslint-disable-next-line no-underscore-dangle
  const locale = window.__widget_simpego?.locale
  const catchAllRoute = {
    path: '*',
    name: 'default',
    redirect: {
      name: 'InsuranceRequest',
    },
  }

  if (locale && locale === 'fr-FR') {
    catchAllRoute.redirect.name = 'InsuranceRequest__fr-FR'
  }
  if (locale && locale === 'it-IT') {
    catchAllRoute.redirect.name = 'InsuranceRequest__it-IT'
  }
  if (locale && locale === 'en-EN') {
    catchAllRoute.redirect.name = 'InsuranceRequest__en-EN'
  }

  return catchAllRoute
}

const translatePath = (path, rootLevel, addPathPrefix, translations) => {
  const translatedPath = path.replace(
    pathRegex,
    (_, prefix, segment, suffix) => prefix + (translations[segment] || segment) + suffix,
  )
  const rootPrefix = (rootLevel && (addPathPrefix || path !== '*')) ? '/' : ''
  const pathPrefix = rootLevel && addPathPrefix ? `${translations.pathPrefix}/` : ''
  return `${rootPrefix}${pathPrefix}${translatedPath}`
}

const translateRoutes = (routeList, language, rootLevel = false) => {
  const result = []
  const { fallbackLocale, messages } = i18n
  routeList.forEach((route) => {
    const nameSuffix = language !== fallbackLocale ? `__${language}` : ''
    const newName = `${route.name}${nameSuffix}`
    const newPath = translatePath(
      route.path,
      rootLevel,
      language !== fallbackLocale,
      messages[language].routes,
    )
    const newRoute = {
      ...route,
      path: newPath,
      name: newName,
      meta: {
        ...route.meta,
        language,
      },
      async beforeEnter(to, from, next) {
        // eslint-disable-next-line no-param-reassign
        i18n.locale = language
        next()
      },
    }
    if (route.children) {
      newRoute.children = translateRoutes(route.children, language)
    }
    if (route.alias) {
      newRoute.alias = translatePath(
        route.alias,
        rootLevel,
        language !== fallbackLocale,
        messages[language].routes,
      )
    }
    if (route.redirect) {
      if (typeof route.redirect === 'function') {
        newRoute.redirect = route.redirect(nameSuffix)
      } else {
        newRoute.redirect = {
          ...route.redirect,
          name: `${route.redirect.name}${nameSuffix}`,
        }
      }
    }
    result.push(newRoute)
  })
  return result
}

const createRouter = () => {
  let languageSpecificRoutes = []
  const { availableLocales } = i18n
  availableLocales.forEach((language) => {
    const translatedRoutes = translateRoutes(routes, language, true)
    languageSpecificRoutes = languageSpecificRoutes.concat(translatedRoutes)
  })

  languageSpecificRoutes.push(generateCatchAllRoute())

  const router = new VueRouter({
    // mode: 'history',
    routes: languageSpecificRoutes,
    scrollBehavior (to, _from, _savedPosition) {
      // scroll to anchor
      if (to.hash) {
        return {
          selector: to.hash,
        }
      }
      // scroll to top
      return { x: 0, y: 0 }
    },
    linkActiveClass: 'is-active',
    linkExactActiveClass: 'is-exact-active',
  })

  // check for parameters within the insurance request and redirect to the start
  router.beforeResolve((to, from, next) => {
    const newRoute = validateAndResolveNewRoute(to)

    if (newRoute) {
      router.replace(localizedRoute(newRoute.routeName, { params: newRoute.routeParams }))
    } else {
      next()
    }
  })

  router.events = new Vue()

  router.afterEach((to, _from) => {
    router.events.$emit('route-change', to.path)
  })

  return router
}

const router = createRouter()

export default router
